import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { catchError, finalize, skip, takeUntil } from 'rxjs/operators';
import { Subject, forkJoin, fromEvent, of } from 'rxjs';
import { Location } from '@angular/common';
import { ManageAction } from '@app-asset-config/components/tagging/tagging.component';
import { TripsService } from '@app-trips/services/trips.service';
import { TripDetailsService } from '@app-trip-details/services/trip-details.service';
import { GetTripDetailsParams } from '@app-trip-details/common/trip-details.model';
import { DataService } from '@app-core/services/data/data.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { BREAKPOINTS_SMALL } from '@app-core/constants/constants';
import { DriverAutoCompleteComponent } from '@app-shared/components/driver-auto-complete/driver-auto-complete.component';
import { UpdateDriverSingleTripBody } from '@app-trips/common/trips.model';
import { MatSnackBarRef } from '@angular/material/snack-bar';

@Component({
  selector: 'app-edit-trip',
  templateUrl: './edit-trip.component.html',
  styleUrls: ['./edit-trip.component.scss'],
})
export class EditTripComponent implements OnInit, OnDestroy {
  public actionName: ManageAction;
  public source: string;
  public tripDetails: any;
  public tripDetailsLoader: boolean = false;
  public updateTagsLoader: boolean = false;
  public updateDriverLoader: boolean = false;
  public tripTags = [];
  public tripTagsIds = [];

  private driverId: string = '';
  private tripId: string = '';
  private tagList: any = [];
  public tagsUpdated: boolean = false;
  public driverUpdated: boolean = false;
  public updatedDriverId: string = '';
  public updatedDriverName: string = '';
  public snackBarRef: MatSnackBarRef<any>;

  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public isMobile = false;
  private driverImageLoaded: boolean = false;
  @ViewChild('imageCanvas', { static: false }) public imageCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('driverImage', { static: false }) public driverImage: ElementRef<HTMLImageElement>;
  @ViewChild(DriverAutoCompleteComponent) public driverAutocomplete!: DriverAutoCompleteComponent;

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private tripsService: TripsService,
    private tripDetailsService: TripDetailsService,
    private snackBarService: SnackBarService,
    public translate: TranslateService,
    public dataService: DataService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params: any) => {
      const { action = '', source = '', tripId = '', driverId = '' } = params;
      this.actionName = action;
      this.source = source;
      this.driverId = driverId;
      this.tripId = tripId;
      this.getTripsTags();
    });

    this.dataService._currentFleet.pipe(skip(1), takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.navigateBack();
    });

    this.breakpointObserver.observe(BREAKPOINTS_SMALL).subscribe((state: BreakpointState) => {
      this.isMobile = state.matches;
    });
    this.subscribeToResize();

    if (this.tripsService?.tripDetails?.tripId === this.tripId) {
      this.tripDetails = this.tripsService.tripDetails;
    } else {
      this.getTripDetails();
    }
  }

  private subscribeToResize() {
    fromEvent(window, 'resize')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.handleCanvasResize();
      });
  }

  public onDriverImageLoad() {
    this.driverImageLoaded = true;
    this.handleCanvasResize();
  }

  public handleCanvasResize() {
    if (this.driverImageLoaded && this.imageCanvas) {
      const canvas = this.imageCanvas.nativeElement;
      const image = this.driverImage.nativeElement;
      canvas.width = image.width;
      canvas.height = image.height;
      this.drawFaceCoordinates();
    }
  }

  public drawFaceCoordinates() {
    const { sampleDriverImageFaceCoordinates = {} } = this.tripDetails || {};
    if (Object.keys(sampleDriverImageFaceCoordinates).length !== 4) {
      return;
    }
    let { topLeftX: x1, topLeftY: y1, bottomRightX: x2, bottomRightY: y2 } = sampleDriverImageFaceCoordinates;

    const canvas = this.imageCanvas.nativeElement;
    const image = this.driverImage.nativeElement;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.beginPath();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#00A300';
      const widthFactor = image.width / 1280;
      const heightFactor = image.height / 720;

      // multiplying by resizeFactor when image resizes
      x1 = x1 * widthFactor;
      y1 = y1 * heightFactor;
      x2 = x2 * widthFactor;
      y2 = y2 * heightFactor;

      // top line
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y1);

      // right line
      ctx.moveTo(x2, y1);
      ctx.lineTo(x2, y2);

      // bottom line
      ctx.moveTo(x2, y2);
      ctx.lineTo(x1, y2);

      // left line
      ctx.moveTo(x1, y2);
      ctx.lineTo(x1, y1);

      ctx.stroke();
    }
  }

  public navigateBack(driverUpdatedSuccesfully?) {
    if (this.source === 'tripDetails' && driverUpdatedSuccesfully) {
      this.tripsService.driverUpdateState = {
        driverUpdatedSuccesfully: driverUpdatedSuccesfully,
        updatedDriverId: this.updatedDriverId,
      };
    }

    this.location.back();
  }

  public selectedTags(tags: any) {
    this.tagList = tags || [];
    this.tagsUpdated = this.hasSelectedTagsChanged();
  }

  private hasSelectedTagsChanged() {
    if (this.tripTagsIds.length !== this.tagList.length) {
      return true;
    }

    if (this.tripTagsIds.length === 0) {
      return false;
    }

    return !this.tripTagsIds.every((tagId, index) => tagId === this.tagList[index]);
  }

  public onSaveDetails() {
    const updateTags$ = this.tagsUpdated ? this.updateTags() : of('');
    const updateDriver$ = this.driverUpdated ? this.updateDriver() : of('');

    forkJoin([updateTags$, updateDriver$])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        ([updateTagsRes, updateDriverRes]) => {
          const snackbarRef: MatSnackBarRef<any> = this.handleTagsResponse(updateTagsRes);
          this.handleDriverResponse(updateDriverRes, snackbarRef);
        },
        () => {
          this.snackBarService.failure(this.translate.instant('editTripDialogueFailed'));
        }
      );
  }

  private handleTagsResponse(response): MatSnackBarRef<any> {
    if (this.tagsUpdated && response) {
      if (response.hasError) {
        return this.snackBarService.failure(this.translate.instant('editTripTagUpdateFailed'));
      }
      if (!this.driverUpdated) {
        this.navigateBack();
        return this.snackBarService.success(this.translate.instant('editTripTagUpdateSuccess'));
      }
      return this.snackBarService.success(this.translate.instant('editTripTagUpdateSuccess'), { duration: 1500 });
    }
  }

  private handleDriverResponse(response, snackBarRef: MatSnackBarRef<any>) {
    if (this.driverUpdated && response) {
      if (snackBarRef) {
        snackBarRef
          .afterDismissed()
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe(() => {
            this.handleDriverResponse1(response);
          });
      } else {
        this.handleDriverResponse1(response);
      }
    }
  }

  private handleDriverResponse1(response) {
    if (response.hasError) {
      if (response.status === 409) {
        this.snackBarService.failure(this.translate.instant('editTripDriverUpdateFailed'));
      } else {
        this.snackBarService.failure(this.translate.instant('editTripDriverUpdateFailedGeneric'));
      }
    } else {
      this.snackBarService.success(this.translate.instant('editTripDriverUpdateSuccess'));
      this.navigateBack(true);
    }
  }

  private updateTags() {
    this.updateTagsLoader = true;
    const body = {
      tripTags: this.tagList,
    };
    return this.tripDetailsService.updateTagsToTrips(this.tripId, body).pipe(
      catchError((err) => {
        return of({ ...err, hasError: true });
      }),
      finalize(() => {
        this.updateTagsLoader = false;
      }),
      takeUntil(this.ngUnsubscribe)
    );
  }

  private updateDriver() {
    this.updateDriverLoader = true;
    const body: UpdateDriverSingleTripBody = {
      newDriverId: this.updatedDriverId,
    };
    return this.tripsService.updateDriverSingleTrip(this.driverId, this.tripId, body).pipe(
      catchError((err) => {
        return of({ ...err, hasError: true });
      }),
      finalize(() => {
        this.updateDriverLoader = false;
      }),
      takeUntil(this.ngUnsubscribe)
    );
  }

  private getTripDetails() {
    this.tripDetailsLoader = true;
    const params = new GetTripDetailsParams({
      tripId: this.tripId,
      driverId: this.driverId,
    });
    this.tripDetailsService
      .getTripDetails(params)
      .pipe(
        finalize(() => {
          this.tripDetailsLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: any) => {
          this.tripDetails = res;
        },
        () => {}
      );
  }

  private getTripsTags() {
    this.tripDetailsLoader = true;
    this.tripDetailsService
      .getTripsTags(this.tripId)
      .pipe(
        finalize(() => {
          this.tripDetailsLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: any) => {
          this.tripTags = res.data?.tripTags;
          this.tripTagsIds = this.tripTags?.map((tag) => tag.tagId) || [];
        },
        () => {}
      );
  }

  public driverChanged(driverId = '') {
    if (driverId.length && driverId !== this.driverId) {
      this.driverUpdated = true;
      this.updatedDriverId = driverId;
      this.updatedDriverName = this.driverAutocomplete.filteredDrivers.find((driver) => {
        return driver.driverId === driverId;
      })?.driverName;
    } else {
      this.driverUpdated = false;
      this.updatedDriverId = '';
      this.updatedDriverName = '';
    }
  }

  public ngOnDestroy(): void {
    this.tripsService.tripDetails = null;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
